import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import { Chat, ChatMessage } from '../types/chat';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
import chatService from '../services/chatService';
import { CHAT_CHA } from '../theme/chatChaavi';
import { VCH } from '../theme/vendorChatChaavi';
import SearchIcon from '@mui/icons-material/Search';

/** Contained gold CTA (matches Explore Chaavi / client dashboard) */
const chaaviGoldCtaSx = {
  borderRadius: '8px',
  textTransform: 'none' as const,
  fontFamily: '"Jost", sans-serif',
  fontSize: '0.8125rem',
  bgcolor: '#c0873a',
  color: '#fff',
  '&:hover': { bgcolor: '#a87030' },
  '& .MuiButton-startIcon': { color: '#fff' },
};

interface ChatComponentProps {
  currentUserPhone: string;
  userType: 'CLIENT' | 'VENDOR';
  /** Cream card layout (sidebar header + split pane). `vendor-chaavi` = HTML mock inside ChaaviVendorDashboardShell. */
  shell?: 'default' | 'chaavi' | 'vendor-chaavi';
}

const ChatComponent: React.FC<ChatComponentProps> = ({
  currentUserPhone,
  userType,
  shell = 'default',
}) => {
  const isChaavi = shell === 'chaavi';
  const isVendorChaavi = shell === 'vendor-chaavi';
  const [searchQuery, setSearchQuery] = useState('');
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createChatOpen, setCreateChatOpen] = useState(false);
  const [newChatData, setNewChatData] = useState({
    clientPhone: '',
    vendorPhone: '',
    businessId: '',
    businessName: '',
    orderId: '',
  });

  const fetchChats = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let chatsData: Chat[];
      if (userType === 'CLIENT') {
        chatsData = await chatService.getChatsByClient(currentUserPhone);
      } else {
        chatsData = await chatService.getChatsByVendor(currentUserPhone);
      }

      setChats(chatsData);
    } catch (err: any) {
      console.error('Error fetching chats:', err);
      setError(err.message || 'Failed to fetch chats');
    } finally {
      setLoading(false);
    }
  }, [currentUserPhone, userType]);

  const fetchMessages = React.useCallback(async (chatId: number) => {
    try {
      setMessagesLoading(true);
      const messagesData = await chatService.getMessages(chatId, currentUserPhone);
      setMessages(messagesData);
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      setError(err.message || 'Failed to fetch messages');
    } finally {
      setMessagesLoading(false);
    }
  }, [currentUserPhone]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const filteredChats = useMemo(() => {
    if (!isVendorChaavi) return chats;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter(
      (c) =>
        (c.otherParticipantName || '').toLowerCase().includes(q) ||
        (c.businessName || '').toLowerCase().includes(q) ||
        (c.lastMessage || '').toLowerCase().includes(q) ||
        (c.clientPhone || '').includes(q)
    );
  }, [chats, searchQuery, isVendorChaavi]);

  const convPanelSubtitle = useMemo(() => {
    if (!isVendorChaavi || chats.length === 0) return '';
    const names = Array.from(new Set(chats.map((c) => c.businessName).filter(Boolean)));
    if (names.length === 1) return `${names[0]} · ${chats.length} chat${chats.length === 1 ? '' : 's'}`;
    return `${names.length} businesses · ${chats.length} chats`;
  }, [chats, isVendorChaavi]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat.chatId);
    }
  }, [selectedChat, fetchMessages]);

  const handleChatSelect = (chat: Chat) => {
    setSelectedChat(chat);
  };

  const handleSendMessage = async (message: string) => {
    if (!selectedChat) return;

    try {
      const messageData = await chatService.sendMessage(
        {
          chatId: selectedChat.chatId,
          message,
          messageType: 'TEXT',
        },
        currentUserPhone
      );

      setMessages(prev => [...prev, messageData]);

      setChats(prev =>
        prev.map(chat =>
          chat.chatId === selectedChat.chatId
            ? { ...chat, lastMessage: message, lastMessageTime: new Date().toISOString() }
            : chat
        )
      );
    } catch (err: any) {
      console.error('Error sending message:', err);
      if (err.message && err.message.includes('Vendors can only reply')) {
        setError('You can only reply to client messages. Please wait for the client to send the first message.');
      } else {
        setError(err.message || 'Failed to send message');
      }
    }
  };

  const handleMarkAsRead = async () => {
    if (!selectedChat) return;

    try {
      await chatService.markMessagesAsRead(selectedChat.chatId, currentUserPhone);

      setMessages(prev =>
        prev.map(msg => ({ ...msg, isRead: true, readAt: new Date().toISOString() }))
      );

      setChats(prev =>
        prev.map(chat =>
          chat.chatId === selectedChat.chatId ? { ...chat, unreadCount: 0 } : chat
        )
      );
    } catch (err: any) {
      console.error('Error marking messages as read:', err);
    }
  };

  const handleCreateChat = async () => {
    try {
      const chat = await chatService.createOrGetChat(
        newChatData.clientPhone,
        newChatData.vendorPhone,
        newChatData.businessId,
        newChatData.businessName,
        newChatData.orderId ? parseInt(newChatData.orderId, 10) : undefined
      );

      setChats(prev => [chat, ...prev]);
      setSelectedChat(chat);
      setCreateChatOpen(false);
      setNewChatData({
        clientPhone: '',
        vendorPhone: '',
        businessId: '',
        businessName: '',
        orderId: '',
      });
    } catch (err: any) {
      console.error('Error creating chat:', err);
      setError(err.message || 'Failed to create chat');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={280} flex={1}>
        <CircularProgress sx={{ color: CHAT_CHA.gold }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2} flex={1}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchChats}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  const sidebarHeader = (
    <Box
      sx={{
        px: 2,
        py: 2,
        borderBottom: `1px solid ${CHAT_CHA.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
        bgcolor: isChaavi ? CHAT_CHA.shellBg : undefined,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontFamily: isChaavi ? '"Jost", sans-serif' : undefined,
          fontWeight: 600,
          color: CHAT_CHA.text,
        }}
      >
        {userType === 'CLIENT' ? 'My Conversations' : 'Business Conversations'}
      </Typography>
      <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
        {userType === 'CLIENT' && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => setCreateChatOpen(true)}
            size="small"
            sx={chaaviGoldCtaSx}
          >
            New Chat
          </Button>
        )}
        <Button
          variant="contained"
          disableElevation
          startIcon={<RefreshIcon />}
          onClick={fetchChats}
          size="small"
          sx={chaaviGoldCtaSx}
        >
          Refresh
        </Button>
      </Box>
    </Box>
  );

  const emptyRightPane = (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
      minHeight={320}
      px={3}
      sx={{ bgcolor: isChaavi ? CHAT_CHA.threadBg : 'grey.50' }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          bgcolor: CHAT_CHA.emptyCircle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <ChatBubbleOutline sx={{ fontSize: 44, color: CHAT_CHA.gold }} />
      </Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontWeight: 400,
          color: CHAT_CHA.text,
        }}
      >
        Select a conversation
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: CHAT_CHA.textMuted,
          textAlign: 'center',
          maxWidth: 320,
          fontFamily: '"Jost", sans-serif',
        }}
      >
        Choose a chat from the list to start messaging
      </Typography>
    </Box>
  );

  if (isVendorChaavi) {
    const emptyVendorChatPane = (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.25,
          bgcolor: VCH.chatAreaBg,
          minHeight: 280,
          borderLeft: { md: `0.5px solid ${VCH.border}` },
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: '#f0ebe0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChatBubbleOutline sx={{ fontSize: 28, color: '#c0b090' }} />
        </Box>
        <Typography sx={{ fontSize: 15, color: VCH.ink, fontFamily: 'sans-serif', fontWeight: 500 }}>
          Select a client chat
        </Typography>
        <Typography sx={{ fontSize: 12, color: VCH.muted, fontFamily: 'sans-serif' }}>
          Choose a conversation from the list
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box
            sx={{
              width: { md: 280 },
              maxWidth: { xs: '100%', md: 280 },
              flexShrink: 0,
              display: { xs: selectedChat ? 'none' : 'flex', md: 'flex' },
              flexDirection: 'column',
              borderRight: { md: `0.5px solid ${VCH.border}` },
              borderBottom: { xs: `0.5px solid ${VCH.border}`, md: 'none' },
              bgcolor: '#fff',
              minHeight: { xs: selectedChat ? 0 : 200, md: 0 },
              minWidth: 0,
            }}
          >
            <Box sx={{ px: 2, py: 1.75, borderBottom: `0.5px solid ${VCH.panelHeadRule}`, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: VCH.ink, fontFamily: 'sans-serif' }}>
                Client conversations
              </Typography>
              <Typography sx={{ fontSize: 11, color: VCH.muted, mt: 0.25, fontFamily: 'sans-serif' }}>
                {convPanelSubtitle || 'Your chats with clients'}
              </Typography>
              <Box sx={{ position: 'relative', mt: 1.25 }}>
                <SearchIcon
                  sx={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 17,
                    color: VCH.muted,
                    pointerEvents: 'none',
                  }}
                />
                <input
                  aria-label="Search clients"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search clients..."
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '7px 10px 7px 34px',
                    borderRadius: 8,
                    border: '0.5px solid #e0d8cc',
                    background: '#fafaf7',
                    fontSize: 12,
                    outline: 'none',
                    color: VCH.ink,
                    fontFamily: 'sans-serif',
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <ChatList
                chats={filteredChats}
                onChatSelect={handleChatSelect}
                selectedChatId={selectedChat?.chatId}
                currentUserPhone={currentUserPhone}
                variant="vendor-chaavi"
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: { xs: selectedChat ? 'flex' : 'none', md: 'flex' },
              flexDirection: 'column',
            }}
          >
            {selectedChat ? (
              <ChatWindow
                chat={selectedChat}
                messages={messages}
                currentUserPhone={currentUserPhone}
                onSendMessage={handleSendMessage}
                onMarkAsRead={handleMarkAsRead}
                isLoading={messagesLoading}
                onCloseThread={() => setSelectedChat(null)}
                variant="vendor-chaavi"
              />
            ) : (
              emptyVendorChatPane
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  if (isChaavi) {
    return (
      <>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            height: '100%',
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              minHeight: 0,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box
              sx={{
                width: { md: 'min(380px, 34%)' },
                flexShrink: 0,
                display: { xs: selectedChat ? 'none' : 'flex', md: 'flex' },
                flexDirection: 'column',
                borderRight: { md: `1px solid ${CHAT_CHA.border}` },
                borderBottom: { xs: `1px solid ${CHAT_CHA.border}`, md: 'none' },
                minHeight: 0,
                bgcolor: CHAT_CHA.shellBg,
              }}
            >
              {sidebarHeader}
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <ChatList
                  chats={chats}
                  onChatSelect={handleChatSelect}
                  selectedChatId={selectedChat?.chatId}
                  currentUserPhone={currentUserPhone}
                  variant="chaavi"
                />
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: { xs: selectedChat ? 'flex' : 'none', md: 'flex' },
                flexDirection: 'column',
              }}
            >
              {selectedChat ? (
                <ChatWindow
                  chat={selectedChat}
                  messages={messages}
                  currentUserPhone={currentUserPhone}
                  onSendMessage={handleSendMessage}
                  onMarkAsRead={handleMarkAsRead}
                  isLoading={messagesLoading}
                  onCloseThread={() => setSelectedChat(null)}
                  variant="chaavi"
                />
              ) : (
                emptyRightPane
              )}
            </Box>
          </Box>
        </Box>

        <Dialog open={createChatOpen} onClose={() => setCreateChatOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Start New Conversation</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              <TextField
                label="Client Phone"
                value={newChatData.clientPhone}
                onChange={(e) => setNewChatData(prev => ({ ...prev, clientPhone: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                label="Vendor Phone"
                value={newChatData.vendorPhone}
                onChange={(e) => setNewChatData(prev => ({ ...prev, vendorPhone: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                label="Business ID"
                value={newChatData.businessId}
                onChange={(e) => setNewChatData(prev => ({ ...prev, businessId: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                label="Business Name"
                value={newChatData.businessName}
                onChange={(e) => setNewChatData(prev => ({ ...prev, businessName: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                label="Order ID (Optional)"
                value={newChatData.orderId}
                onChange={(e) => setNewChatData(prev => ({ ...prev, orderId: e.target.value }))}
                fullWidth
                type="number"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateChatOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateChat}
              variant="contained"
              disabled={
                !newChatData.clientPhone ||
                !newChatData.vendorPhone ||
                !newChatData.businessId ||
                !newChatData.businessName
              }
            >
              Create Chat
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  /* Legacy layout for vendor page and other embeds */
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: 2,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {userType === 'CLIENT' ? 'My Conversations' : 'Business Conversations'}
          </Typography>
          <Box display="flex" gap={1}>
            {userType === 'CLIENT' && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={() => setCreateChatOpen(true)}
                size="small"
                sx={chaaviGoldCtaSx}
              >
                New Chat
              </Button>
            )}
            <Button
              variant="contained"
              disableElevation
              startIcon={<RefreshIcon />}
              onClick={fetchChats}
              size="small"
              sx={chaaviGoldCtaSx}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box sx={{ width: { xs: '100%', md: '33.333%' }, borderRight: 1, borderColor: 'divider', minHeight: 0, overflow: 'hidden' }}>
          <ChatList
            chats={chats}
            onChatSelect={handleChatSelect}
            selectedChatId={selectedChat?.chatId}
            currentUserPhone={currentUserPhone}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedChat ? (
            <ChatWindow
              chat={selectedChat}
              messages={messages}
              currentUserPhone={currentUserPhone}
              onSendMessage={handleSendMessage}
              onMarkAsRead={handleMarkAsRead}
              isLoading={messagesLoading}
            />
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              color="text.secondary"
            >
              <Typography variant="h6" gutterBottom>
                Select a conversation
              </Typography>
              <Typography variant="body2">Choose a chat from the list to start messaging</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={createChatOpen} onClose={() => setCreateChatOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start New Conversation</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Client Phone"
              value={newChatData.clientPhone}
              onChange={(e) => setNewChatData(prev => ({ ...prev, clientPhone: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Vendor Phone"
              value={newChatData.vendorPhone}
              onChange={(e) => setNewChatData(prev => ({ ...prev, vendorPhone: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Business ID"
              value={newChatData.businessId}
              onChange={(e) => setNewChatData(prev => ({ ...prev, businessId: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Business Name"
              value={newChatData.businessName}
              onChange={(e) => setNewChatData(prev => ({ ...prev, businessName: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Order ID (Optional)"
              value={newChatData.orderId}
              onChange={(e) => setNewChatData(prev => ({ ...prev, orderId: e.target.value }))}
              fullWidth
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateChatOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateChat}
            variant="contained"
            disabled={
              !newChatData.clientPhone ||
              !newChatData.vendorPhone ||
              !newChatData.businessId ||
              !newChatData.businessName
            }
          >
            Create Chat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatComponent;
